// Item 406 (design §6.1) — a suspicious event in the dashboard's Suspicious block. The shape of CriticalEventRow in
// quiet orange (index.css .sus-row): a solid bar on the left, a small "SUSPICIOUS · Person · standing still 12 s" label,
// the camera in bold, time, and a separate Seen button. No running light and no arrival glow, whatever the state: the
// alarm keeps its meaning. Seen: muted, "Seen by <name> · <time>", no button. Opening the event and Seen are two
// separate real buttons (a button inside a button is invalid HTML and breaks keyboard use).
import { forwardRef, type HTMLAttributes } from 'react'
import { useTranslation } from 'react-i18next'
import { Activity } from 'lucide-react'
import type { Event } from '@/types'
import { cn, formatCardDateTime } from '@/lib/utils'
import { isOpenSuspicious } from '@/lib/suspiciousEvents'
import { useClueReasons, useMarkSeen } from '@/hooks/useSuspiciousEvents'
import type { RecentEventPreviewRowProps } from './RecentEventPreview'

/** "Seen by <name> · <time>" of a seen suspicious event (row, card, popup): emerald, like the acknowledged line. */
export function SuspiciousSeenLine({ event, id, className }: { event: Event; id?: string; className?: string }) {
  const { t } = useTranslation('common')
  if (!event.seen_at) return null
  const time = formatCardDateTime(event.seen_at)
  return (
    <p id={id} className={cn('text-[13px] font-semibold leading-snug text-emerald-700 dark:text-emerald-400', className)}>
      {event.seen_by ? t('suspicious.seenBy', { name: event.seen_by, time }) : t('suspicious.seenNoName', { time })}
    </p>
  )
}

/**
 * The event popup's part of a suspicious event: an orange chip per clue that said yes, the seen line, Seen while open.
 * Its own component so a popup of any other event never needs a query client. The server's answer to Seen is shown
 * directly: on the Dashboard a seen event can leave the list behind the popup, which then holds on to the old copy.
 */
export function SuspiciousDetails({ event }: { event: Event }) {
  const { t } = useTranslation('common')
  const clueReasons = useClueReasons()
  const markSeen = useMarkSeen()
  const shown = markSeen.data?.id === event.id ? markSeen.data : event
  const reasons = clueReasons(event)
  return (
    <div data-suspicious-details className="mt-1 space-y-1">
      {reasons.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {reasons.map((reason) => <span key={reason} className="sus-reason">{reason}</span>)}
        </div>
      )}
      {isOpenSuspicious(shown) ? (
        <button
          type="button"
          className="sus-seen h-11 wide:h-9"
          onClick={() => markSeen.mutate(event.id)}
          disabled={markSeen.isPending}
        >
          {t('suspicious.seen')}
        </button>
      ) : (
        <SuspiciousSeenLine event={shown} />
      )}
    </div>
  )
}

interface Props extends HTMLAttributes<HTMLDivElement> {
  event: Event
  /** The hover / focus preview's handlers (RecentEventPreview); they go on the open button. */
  row: RecentEventPreviewRowProps
  onOpen: () => void
  onSeen: () => void
  seeing?: boolean
}

export const SuspiciousEventRow = forwardRef<HTMLDivElement, Props>(function SuspiciousEventRow(
  { event, row, onOpen, onSeen, seeing, className, ...rest }, ref,
) {
  const { t } = useTranslation('common')
  const clueReasons = useClueReasons()
  const open = isOpenSuspicious(event)
  const reasons = clueReasons(event)

  return (
    <div
      ref={ref}
      {...rest}
      data-suspicious-row={open ? 'open' : 'seen'}
      className={cn(
        'sus-row flex w-full flex-wrap items-center gap-x-3 gap-y-2 rounded-lg py-2.5 pl-3.5 pr-2.5 sm:gap-x-4 sm:py-3 my-1',
        !open && 'sus-seen-row',
        className,
      )}
    >
      <button
        type="button"
        {...row}
        onClick={() => { row.onClick(); onOpen() }}
        className={cn(
          'flex min-w-full sm:min-w-[13rem] flex-1 items-center gap-3 sm:gap-4 rounded-md text-left',
          'focus:outline-none focus-visible:ring-2 focus-visible:ring-primary',
        )}
      >
        {event.thumbnail_url ? (
          <img
            src={event.thumbnail_url}
            alt=""
            loading="lazy"
            className="h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 rounded-md object-cover bg-muted"
          />
        ) : (
          <div className="flex h-16 w-24 sm:h-20 sm:w-[120px] flex-shrink-0 items-center justify-center rounded-md bg-orange-500/10">
            <Activity className="h-5 w-5 text-orange-700 dark:text-orange-300" />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <span className="sus-label">
            <b>{t('suspicious.label')}</b>{reasons.length > 0 ? ` · ${reasons.join(' · ')}` : null}
          </span>
          <div className="mt-0.5 flex flex-col sm:flex-row sm:items-baseline xl:flex-col xl:items-stretch 2xl:flex-row 2xl:items-baseline justify-between gap-0.5 sm:gap-3 xl:gap-0.5 2xl:gap-3">
            <p className="flex min-w-0 items-baseline gap-2 text-[15px] leading-snug">
              <strong className="truncate font-bold">{event.camera_name}</strong>
              {event.camera_group && <span className="sus-muted truncate text-[13px]">· {event.camera_group}</span>}
            </p>
            <span
              className="sus-muted text-xs sm:text-[13px] whitespace-nowrap tabular-nums flex-shrink-0"
              title={new Date(event.occurred_at).toLocaleString()}
            >
              {formatCardDateTime(event.occurred_at)}
            </span>
          </div>
          {!open && <SuspiciousSeenLine event={event} className="mt-1" />}
        </div>
      </button>
      {open && (
        <button type="button" className="sus-seen ml-auto" onClick={onSeen} disabled={seeing}>
          {t('suspicious.seen')}
        </button>
      )}
    </div>
  )
})
